import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { App, LocAR } from 'locar';
import { Focus, LocateFixed, RotateCcw, MoveUp, Minus, Plus, Rotate3D, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, SlidersHorizontal, Replace } from 'lucide';

type IconType = [tag: string, attrs: Record<string, string | number | undefined>][];
function iconToSvg(icon: IconType, attrs: Record<string, string | number> = {}): string {
    const defaultAttrs: Record<string, string | number> = {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        width: '18',
        height: '18',
    };
    const svgAttrs = { ...defaultAttrs, ...attrs };
    const attrsStr = Object.entries(svgAttrs)
        .map(([k, v]) => `${k}="${v}"`)
        .join(' ');
    const paths = icon.map(([tag, elAttrs]) => {
        const elStr = Object.entries(elAttrs)
            .map(([k, v]) => `${k}="${v}"`)
            .join(' ');
        return `<${tag} ${elStr}/>`;
    }).join('\n  ');
    return `<svg ${attrsStr}>\n  ${paths}\n</svg>`;
}

interface GpsReceivedEvent {
    position: GeolocationPosition;
    distMoved: number;
}

// ===== CONFIGURATION =====
const TARGET_LAT = 54.733838004449815;
const TARGET_LNG = 20.469524130835975;
const MODEL_PATH = '/models/cars/scene.gltf';
const INITIAL_SCALE = 1.0;
const MODEL_ALTITUDE = 0;

// ===== DOM ELEMENTS =====
const statusDot = document.getElementById('status-dot') as HTMLElement;
const statusText = document.getElementById('status-text') as HTMLElement;
const coordsInfo = document.getElementById('coords-info') as HTMLElement;
const scaleSlider = document.getElementById('scale-slider') as HTMLInputElement;
const scaleValue = document.getElementById('scale-value') as HTMLElement;
const rotationSlider = document.getElementById('rotation-slider') as HTMLInputElement;
const rotationValue = document.getElementById('rotation-value') as HTMLElement;
const offsetLatSlider = document.getElementById('offset-lat-slider') as HTMLInputElement;
const offsetLatValue = document.getElementById('offset-lat-value') as HTMLElement;
const offsetLngSlider = document.getElementById('offset-lng-slider') as HTMLInputElement;
const offsetLngValue = document.getElementById('offset-lng-value') as HTMLElement;
const toolsPanel = document.getElementById('tools-panel') as HTMLElement;
const btnToggleTools = document.getElementById('btn-toggle-tools') as HTMLButtonElement;
const btnCameraRotate = document.getElementById('btn-camera-rotate') as HTMLButtonElement;
const compassIcon = document.getElementById('compass-icon') as HTMLElement;
const compassWrapper = document.getElementById('compass-wrapper') as HTMLElement;
const btnReset = document.getElementById('btn-reset') as HTMLButtonElement;
const btnReplaceModel = document.getElementById('btn-replace-model') as HTMLButtonElement;
const btnFakeGps = document.getElementById('btn-fake-gps') as HTMLButtonElement;
const btnScaleMinus = document.getElementById('btn-scale-minus') as HTMLButtonElement;
const btnScalePlus = document.getElementById('btn-scale-plus') as HTMLButtonElement;
const btnRotationMinus = document.getElementById('btn-rotation-minus') as HTMLButtonElement;
const btnRotationPlus = document.getElementById('btn-rotation-plus') as HTMLButtonElement;
const btnOffsetLatMinus = document.getElementById('btn-offset-lat-minus') as HTMLButtonElement;
const btnOffsetLatPlus = document.getElementById('btn-offset-lat-plus') as HTMLButtonElement;
const btnOffsetLngMinus = document.getElementById('btn-offset-lng-minus') as HTMLButtonElement;
const btnOffsetLngPlus = document.getElementById('btn-offset-lng-plus') as HTMLButtonElement;
const heightSlider = document.getElementById('height-slider') as HTMLInputElement;
const heightValue = document.getElementById('height-value') as HTMLElement;
const btnHeightMinus = document.getElementById('btn-height-minus') as HTMLButtonElement;
const btnHeightPlus = document.getElementById('btn-height-plus') as HTMLButtonElement;

// ===== ICONS =====
btnToggleTools.innerHTML = iconToSvg(SlidersHorizontal, { 'stroke-width': '2', width: '20', height: '20' });
btnCameraRotate.innerHTML = iconToSvg(Rotate3D, { 'stroke-width': '2', width: '20', height: '20' });
btnFakeGps.innerHTML = iconToSvg(LocateFixed, { 'stroke-width': '2', width: '20', height: '20' });
btnReset.innerHTML = iconToSvg(RotateCcw, { 'stroke-width': '2', width: '20', height: '20' });
btnReplaceModel.innerHTML = iconToSvg(Replace, { 'stroke-width': '2', width: '20', height: '20' });
if (compassIcon) {
    compassIcon.insertAdjacentHTML('afterbegin', iconToSvg(MoveUp, { 'stroke-width': '2.5', width: '24', height: '24' }));
}
btnScaleMinus.innerHTML = iconToSvg(Minus, { 'stroke-width': '2.5', width: '20', height: '20' });
btnScalePlus.innerHTML = iconToSvg(Plus, { 'stroke-width': '2.5', width: '20', height: '20' });
btnRotationMinus.innerHTML = iconToSvg(Minus, { 'stroke-width': '2.5', width: '20', height: '20' });
btnRotationPlus.innerHTML = iconToSvg(Plus, { 'stroke-width': '2.5', width: '20', height: '20' });
btnOffsetLatMinus.innerHTML = iconToSvg(ArrowDown, { 'stroke-width': '2.5', width: '20', height: '20' });
btnOffsetLatPlus.innerHTML = iconToSvg(ArrowUp, { 'stroke-width': '2.5', width: '20', height: '20' });
btnOffsetLngMinus.innerHTML = iconToSvg(ArrowLeft, { 'stroke-width': '2.5', width: '20', height: '20' });
btnOffsetLngPlus.innerHTML = iconToSvg(ArrowRight, { 'stroke-width': '2.5', width: '20', height: '20' });
btnHeightMinus.innerHTML = iconToSvg(Minus, { 'stroke-width': '2.5', width: '20', height: '20' });
btnHeightPlus.innerHTML = iconToSvg(Plus, { 'stroke-width': '2.5', width: '20', height: '20' });

setModelButtons(false);

// ===== STATE =====
let locar: LocAR | null = null;
let modelContainer: THREE.Group | null = null;
let currentScale = INITIAL_SCALE;
let currentRotation = 0;
let currentOffsetLat = 0;
let currentOffsetLng = 0;
let userLat: number | null = null;
let userLng: number | null = null;
let userAlt: number | null = null;
let userAltAcc: number | null = null;
let modelAltitude: number | null = null;
let currentHeight = 0;
let currentBearing = 0;
let modelLat: number | null = null;
let modelLng: number | null = null;
let gpsReady = false;
let useFakeGps = false;
let cameraRotateEnabled = false;
let isModelLoading = false;
let toolsPanelVisible = false;

// ===== DIMENSIONS =====
let dimensionsGroup: THREE.Group | null = null;
let axesHelper: THREE.AxesHelper | null = null;
let dimensionLines: THREE.Line[] = [];
let dimensionSprites: THREE.Sprite[] = [];
const DIMENSION_OFFSET = 0.3;
const TICK_LENGTH = 0.08;

// ===== GEODESIC HELPERS =====
function toRad(deg: number): number {
    return deg * Math.PI / 180;
}

function toDeg(rad: number): number {
    return rad * 180 / Math.PI;
}

function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function getBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const dLng = toRad(lng2 - lng1);
    const y = Math.sin(dLng) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
        Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// ===== ELEVATION =====
async function fetchElevation(lat: number, lng: number): Promise<number> {
    try {
        const res = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lat}&longitude=${lng}`);
        const data = await res.json();
        if (data.elevation && data.elevation.length > 0) {
            return data.elevation[0];
        }
    } catch (e) {
        console.warn('Не удалось получить высоту модели:', e);
    }
    return MODEL_ALTITUDE;
}

// ===== UI UPDATES =====
function updateStatus(state: string, text: string) {
    statusDot.className = 'dot ' + state;
    statusText.textContent = text;
}

function updateCoordsInfo() {
    if (userLat === null || userLng === null || !modelContainer) {
        compassIcon.style.display = 'none';
        compassWrapper.style.display = 'none';
        return;
    }

    const lat = userLat! + currentOffsetLat;
    const lng = userLng! + currentOffsetLng;

    const effLat = modelLat !== null ? modelLat : TARGET_LAT + currentOffsetLat;
    const effLng = modelLng !== null ? modelLng : TARGET_LNG + currentOffsetLng;

    const distance = getDistance(lat, lng, effLat, effLng);
    let bearing = getBearing(lat, lng, effLat, effLng);

    if (useFakeGps && locar) {
        const camDeg = THREE.MathUtils.radToDeg(locar.camera.rotation.y);
        bearing = (bearing + camDeg + 360) % 360;
    }

    const diffLat = Math.abs(effLat - TARGET_LAT) > 0.0000001;
    const diffLng = Math.abs(effLng - TARGET_LNG) > 0.0000001;
    const diffAltitude = Math.abs(currentHeight - MODEL_ALTITUDE) > 0.001;
    const effColor = (diffLat || diffLng) ? 'var(--color-orange)' : '#fff';
    const altitudeColor = diffAltitude ? 'var(--color-orange)' : '#fff';

    const altText = typeof userAlt === 'number'
        ? `, ${userAlt.toFixed(1)} м ± ${typeof userAltAcc === 'number' ? userAltAcc.toFixed(1) : '—'} м`
        : '';

    const modelAltText = modelAltitude !== null
        ? `, Высота: <span style="color: ${altitudeColor}">${currentHeight.toFixed(1)} м</span> (API: ${modelAltitude.toFixed(1)} м)`
        : `, Высота: <span style="color: ${altitudeColor}">${currentHeight.toFixed(1)} м</span>`;

    coordsInfo.innerHTML =
        `<p>Вы: ${(userLat!).toFixed(6)}, ${(userLng!).toFixed(6)}${altText}</p>` +
        `<p>Цель: <span style="color: ${effColor}">${effLat.toFixed(6)}, ${effLng.toFixed(6)}</span>${modelAltText}</p>` +
        `<p>Расстояние: ${distance.toFixed(1)} м, Пеленг: ${bearing.toFixed(1)}°</p>`;

    currentBearing = bearing;

    compassIcon.style.display = 'flex';
    compassWrapper.style.display = 'flex';
    compassIcon.style.transform = `rotate(${bearing}deg)`;
}

// ===== POSITION MODEL =====
function positionModel() {
    if (userLat === null || userLng === null || !modelContainer) return;

    modelContainer.scale.setScalar(currentScale);
    modelContainer.rotation.y = THREE.MathUtils.degToRad(currentRotation);

    updateCoordsInfo();
}

// ===== TOOLS =====
let modelBasePosition: { x: number; z: number } | null = null;

function updateScale() {
    scaleValue.textContent = currentScale.toFixed(2) + 'x';
    if (modelContainer) {
        modelContainer.scale.setScalar(currentScale);
    }
    updateDimensions();
    updateDimensionText();
}

function updateRotation() {
    if (!modelContainer) return;
    modelContainer.rotation.y = THREE.MathUtils.degToRad(currentRotation);
    rotationValue.textContent = currentRotation.toFixed(1) + '°';
    updateDimensions();
    updateDimensionText();
}

function updateOffsetLat() {
    offsetLatValue.textContent = currentOffsetLat.toFixed(6) + '°';
    if (modelContainer && modelBasePosition) {
        const metersPerDegLat = 111320;
        modelContainer.position.x = modelBasePosition.x + currentOffsetLat * metersPerDegLat;
    }
    updateCoordsInfo();
    updateDimensions();
    updateDimensionText();
}

function updateOffsetLng() {
    offsetLngValue.textContent = currentOffsetLng.toFixed(6) + '°';
    if (modelContainer && modelBasePosition) {
        const metersPerDegLng = 111320 * Math.cos(toRad(TARGET_LAT));
        modelContainer.position.z = modelBasePosition.z + currentOffsetLng * metersPerDegLng;
    }
    updateCoordsInfo();
    updateDimensions();
    updateDimensionText();
}

function updateHeight() {
    heightValue.textContent = currentHeight.toFixed(1) + ' м';
    if (modelContainer) {
        modelContainer.position.y = currentHeight;
    }
    updateCoordsInfo();
}

function resetAll() {
    currentScale = INITIAL_SCALE;
    currentRotation = 0;
    currentOffsetLat = 0;
    currentOffsetLng = 0;

    scaleSlider.value = INITIAL_SCALE.toString();
    scaleValue.textContent = INITIAL_SCALE.toFixed(2) + 'x';
    rotationSlider.value = '0';
    rotationValue.textContent = '0°';
    offsetLatSlider.value = '0';
    offsetLatValue.textContent = '0.000000°';
    offsetLngSlider.value = '0';
    offsetLngValue.textContent = '0.000000°';
    currentHeight = 0;
    heightSlider.value = '0';
    heightValue.textContent = '0.0 м';
    modelLat = null;
    modelLng = null;

    if (modelContainer && modelBasePosition) {
        modelContainer.scale.setScalar(currentScale);
        modelContainer.rotation.y = 0;
        modelContainer.position.y = 0;

        if (locar) {
            locar.add(modelContainer, TARGET_LNG, TARGET_LAT, MODEL_ALTITUDE);
            modelBasePosition = { x: modelContainer.position.x, z: modelContainer.position.z };
        }
    }

    positionModel();
    updateDimensions();
    updateDimensionText();
}

// ===== TOOLS PANEL =====
function toggleToolsPanel() {
    toolsPanelVisible = !toolsPanelVisible;
    toolsPanel.classList.toggle('visible', toolsPanelVisible);
    btnToggleTools.classList.toggle('active', toolsPanelVisible);
    if (dimensionsGroup) {
        dimensionsGroup.visible = toolsPanelVisible;
    }
}

// ===== FAKE GPS =====
function toggleFakeGps() {
    if (!locar) return;

    useFakeGps = !useFakeGps;
    cameraRotateEnabled = useFakeGps;
    updateCameraRotateIcon();

    if (useFakeGps) {
        const offsetLng = 5 / (111320 * Math.cos(toRad(TARGET_LAT)));
        const fakeLng = TARGET_LNG + offsetLng;
        const fakeLat = TARGET_LAT;

        locar.fakeGps(fakeLng, fakeLat);
        userLat = fakeLat;
        userLng = fakeLng;
        btnFakeGps.innerHTML = iconToSvg(LocateFixed, { 'stroke-width': '2', width: '20', height: '20' });
        btnFakeGps.classList.add('active');
        updateStatus('ready', 'Fake GPS активирован');
        loadModel();
        positionModel();
    } else {
        btnFakeGps.innerHTML = iconToSvg(LocateFixed, { 'stroke-width': '2', width: '20', height: '20' });
        btnFakeGps.classList.remove('active');
        updateStatus('', 'Переключение на реальный GPS...');
        userLat = null;
        userLng = null;
        userAlt = null;
        userAltAcc = null;
        gpsReady = false;
        setModelButtons(false);
        if (modelContainer) {
            modelContainer.visible = false;
        }
        locar.startGps();
    }
}

// ===== CAMERA ROTATE ICON =====
function updateCameraRotateIcon() {
    btnCameraRotate.classList.toggle('active', cameraRotateEnabled);
    btnCameraRotate.innerHTML = iconToSvg(
        cameraRotateEnabled ? Rotate3D : Focus,
        { 'stroke-width': '2', width: '20', height: '20' }
    );
}

function setModelButtons(enabled: boolean) {
    btnToggleTools.disabled = !enabled;
    btnCameraRotate.disabled = !enabled;
    btnReset.disabled = !enabled;
    btnReplaceModel.disabled = !enabled;
    btnToggleTools.style.opacity = enabled ? '1' : '0.3';
    btnCameraRotate.style.opacity = enabled ? '1' : '0.3';
    btnReset.style.opacity = enabled ? '1' : '0.3';
    btnReplaceModel.style.opacity = enabled ? '1' : '0.3';
}

// ===== DIMENSIONS =====
function createTextSprite(text: string, color: string = '#ffffff'): THREE.Sprite {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const isMobile = window.innerWidth < 768;
    const fontSize = isMobile ? 64 : 96;
    const padding = isMobile ? 24 : 32;
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;
    canvas.width = textWidth + padding * 2;
    canvas.height = fontSize + padding * 2;

    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 4;
    ctx.shadowOffsetY = 4;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(material);
    const aspect = canvas.width / canvas.height;
    sprite.scale.set(aspect * 0.8, 0.8, 1);
    return sprite;
}

function createDimensionLine(start: THREE.Vector3, end: THREE.Vector3, color: string): THREE.Line {
    const points = [start, end];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const isMobile = window.innerWidth < 768;
    const material = new THREE.LineBasicMaterial({ color: new THREE.Color(color), linewidth: isMobile ? 4 : 2 });
    return new THREE.Line(geometry, material);
}

function createTick(start: THREE.Vector3, direction: THREE.Vector3, length: number, color: string): THREE.Line {
    const end = start.clone().add(direction.clone().normalize().multiplyScalar(length));
    return createDimensionLine(start, end, color);
}

function createDimensions() {
    if (!modelContainer || !locar) return;

    dimensionsGroup = new THREE.Group();
    axesHelper = new THREE.AxesHelper(1);
    dimensionsGroup.add(axesHelper!);

    const box = new THREE.Box3().setFromObject(modelContainer);
    const size = new THREE.Vector3();
    box.getSize(size);

    const minX = box.min.x;
    const minY = box.min.y;
    const minZ = box.min.z;

    const width = size.x;
    const height = size.y;
    const depth = size.z;

    // X dimension (width) - along X axis at bottom-front
    const xStart = new THREE.Vector3(minX, minY, minZ);
    const xEnd = new THREE.Vector3(minX + width, minY, minZ);
    const xLine = createDimensionLine(xStart, xEnd, '#ff0000');
    dimensionsGroup.add(xLine);
    dimensionLines.push(xLine);

    // X ticks
    const xTick1 = createTick(xStart, new THREE.Vector3(0, 1, 0), TICK_LENGTH, '#ff0000');
    dimensionsGroup.add(xTick1);
    dimensionLines.push(xTick1);
    const xTick2 = createTick(xEnd, new THREE.Vector3(0, 1, 0), TICK_LENGTH, '#ff0000');
    dimensionsGroup.add(xTick2);
    dimensionLines.push(xTick2);

    // X text
    const xText = createTextSprite(`Ширина: ${width.toFixed(2)} м`);
    xText.position.set(minX + width / 2, minY - DIMENSION_OFFSET, minZ);
    dimensionsGroup.add(xText);
    dimensionSprites.push(xText);

    // Y dimension (height) - along Y axis at left-front
    const yStart = new THREE.Vector3(minX, minY, minZ);
    const yEnd = new THREE.Vector3(minX, minY + height, minZ);
    const yLine = createDimensionLine(yStart, yEnd, '#00ff00');
    dimensionsGroup.add(yLine);
    dimensionLines.push(yLine);

    // Y ticks
    const yTick1 = createTick(yStart, new THREE.Vector3(1, 0, 0), TICK_LENGTH, '#00ff00');
    dimensionsGroup.add(yTick1);
    dimensionLines.push(yTick1);
    const yTick2 = createTick(yEnd, new THREE.Vector3(1, 0, 0), TICK_LENGTH, '#00ff00');
    dimensionsGroup.add(yTick2);
    dimensionLines.push(yTick2);

    // Y text
    const yText = createTextSprite(`Высота: ${height.toFixed(2)} м`);
    yText.position.set(minX - DIMENSION_OFFSET, minY + height / 2, minZ);
    dimensionsGroup.add(yText);
    dimensionSprites.push(yText);

    // Z dimension (depth) - along Z axis at bottom-left
    const zStart = new THREE.Vector3(minX, minY, minZ);
    const zEnd = new THREE.Vector3(minX, minY, minZ + depth);
    const zLine = createDimensionLine(zStart, zEnd, '#0000ff');
    dimensionsGroup.add(zLine);
    dimensionLines.push(zLine);

    // Z ticks
    const zTick1 = createTick(zStart, new THREE.Vector3(1, 0, 0), TICK_LENGTH, '#0000ff');
    dimensionsGroup.add(zTick1);
    dimensionLines.push(zTick1);
    const zTick2 = createTick(zEnd, new THREE.Vector3(1, 0, 0), TICK_LENGTH, '#0000ff');
    dimensionsGroup.add(zTick2);
    dimensionLines.push(zTick2);

    // Z text
    const zText = createTextSprite(`Глубина: ${depth.toFixed(2)} м`);
    zText.position.set(minX - DIMENSION_OFFSET, minY, minZ + depth / 2);
    dimensionsGroup.add(zText);
    dimensionSprites.push(zText);

    if (locar) {
        locar.scene.add(dimensionsGroup);
        dimensionsGroup.visible = false;
    }
}

function updateDimensions() {
    if (!dimensionsGroup || !modelContainer) return;

    const box = new THREE.Box3().setFromObject(modelContainer);
    const size = new THREE.Vector3();
    box.getSize(size);

    const minX = box.min.x;
    const minY = box.min.y;
    const minZ = box.min.z;

    const width = size.x;
    const height = size.y;
    const depth = size.z;

    // Update axes position
    if (axesHelper) {
        axesHelper.position.set(minX, minY, minZ);
    }

    // Update X dimension
    const xStart = new THREE.Vector3(minX, minY, minZ);
    const xEnd = new THREE.Vector3(minX + width, minY, minZ);
    dimensionLines[0].geometry.setFromPoints([xStart, xEnd]);
    dimensionLines[0].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[0].material as THREE.LineBasicMaterial).color.set('#ff0000');

    dimensionLines[1].geometry.setFromPoints([
        new THREE.Vector3(minX, minY, minZ),
        new THREE.Vector3(minX, minY + TICK_LENGTH, minZ)
    ]);
    dimensionLines[1].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[1].material as THREE.LineBasicMaterial).color.set('#ff0000');

    dimensionLines[2].geometry.setFromPoints([
        new THREE.Vector3(minX + width, minY, minZ),
        new THREE.Vector3(minX + width, minY + TICK_LENGTH, minZ)
    ]);
    dimensionLines[2].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[2].material as THREE.LineBasicMaterial).color.set('#ff0000');

    dimensionSprites[0].position.set(minX + width / 2, minY - DIMENSION_OFFSET, minZ);

    // Update Y dimension
    const yStart = new THREE.Vector3(minX, minY, minZ);
    const yEnd = new THREE.Vector3(minX, minY + height, minZ);
    dimensionLines[3].geometry.setFromPoints([yStart, yEnd]);
    dimensionLines[3].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[3].material as THREE.LineBasicMaterial).color.set('#00ff00');

    dimensionLines[4].geometry.setFromPoints([
        new THREE.Vector3(minX, minY, minZ),
        new THREE.Vector3(minX + TICK_LENGTH, minY, minZ)
    ]);
    dimensionLines[4].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[4].material as THREE.LineBasicMaterial).color.set('#00ff00');

    dimensionLines[5].geometry.setFromPoints([
        new THREE.Vector3(minX, minY + height, minZ),
        new THREE.Vector3(minX + TICK_LENGTH, minY + height, minZ)
    ]);
    dimensionLines[5].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[5].material as THREE.LineBasicMaterial).color.set('#00ff00');

    dimensionSprites[1].position.set(minX - DIMENSION_OFFSET, minY + height / 2, minZ);

    // Update Z dimension
    const zStart = new THREE.Vector3(minX, minY, minZ);
    const zEnd = new THREE.Vector3(minX, minY, minZ + depth);
    dimensionLines[6].geometry.setFromPoints([zStart, zEnd]);
    dimensionLines[6].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[6].material as THREE.LineBasicMaterial).color.set('#0000ff');

    dimensionLines[7].geometry.setFromPoints([
        new THREE.Vector3(minX, minY, minZ),
        new THREE.Vector3(minX + TICK_LENGTH, minY, minZ)
    ]);
    dimensionLines[7].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[7].material as THREE.LineBasicMaterial).color.set('#0000ff');

    dimensionLines[8].geometry.setFromPoints([
        new THREE.Vector3(minX, minY, minZ + depth),
        new THREE.Vector3(minX + TICK_LENGTH, minY, minZ + depth)
    ]);
    dimensionLines[8].geometry.attributes.position.needsUpdate = true;
    (dimensionLines[8].material as THREE.LineBasicMaterial).color.set('#0000ff');

    dimensionSprites[2].position.set(minX - DIMENSION_OFFSET, minY, minZ + depth / 2);
}

function updateDimensionText() {
    if (!dimensionsGroup || !modelContainer) return;

    const box = new THREE.Box3().setFromObject(modelContainer);
    const size = new THREE.Vector3();
    box.getSize(size);

    const width = size.x;
    const height = size.y;
    const depth = size.z;

    // Update X text
    const tex0 = dimensionSprites[0].material.map as any;
    if (tex0 && tex0.image && tex0.image.parentNode) {
        tex0.dispose();
    }
    dimensionSprites[0].material.map = createTextSprite(`Ширина: ${width.toFixed(2)} м`).material.map;
    dimensionSprites[0].material.needsUpdate = true;

    // Update Y text
    const tex1 = dimensionSprites[1].material.map as any;
    if (tex1 && tex1.image && tex1.image.parentNode) {
        tex1.dispose();
    }
    dimensionSprites[1].material.map = createTextSprite(`Высота: ${height.toFixed(2)} м`).material.map;
    dimensionSprites[1].material.needsUpdate = true;

    // Update Z text
    const tex2 = dimensionSprites[2].material.map as any;
    if (tex2 && tex2.image && tex2.image.parentNode) {
        tex2.dispose();
    }
    dimensionSprites[2].material.map = createTextSprite(`Глубина: ${depth.toFixed(2)} м`).material.map;
    dimensionSprites[2].material.needsUpdate = true;
}

// ===== LOAD MODEL =====
function loadModel() {
    if (modelContainer || isModelLoading) return;
    isModelLoading = true;
    const manager = new THREE.LoadingManager();
    const loader = new GLTFLoader(manager);

    loader.load(
        MODEL_PATH,
        (gltf) => {
            isModelLoading = false;
            modelContainer = new THREE.Group();
            modelContainer.add(gltf.scene);
            modelContainer.scale.setScalar(currentScale);
            modelContainer.rotation.y = THREE.MathUtils.degToRad(currentRotation);
            modelContainer.visible = true;

            // Ensure materials are properly configured
            modelContainer.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    const mesh = child as THREE.Mesh;
                    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                    materials.forEach((mat) => {
                        mat.needsUpdate = true;
                        const tex = (mat as any).map;
                        if (tex) {
                            console.log('Material:', mat.type, 'has texture');
                        }
                    });
                }
            });

            if (locar) {
                locar.add(modelContainer, TARGET_LNG, TARGET_LAT, MODEL_ALTITUDE);
                modelBasePosition = { x: modelContainer.position.x, z: modelContainer.position.z };
                updateOffsetLat();
                updateOffsetLng();
                createDimensions();
                updateDimensions();
                updateDimensionText();
            }

            console.log('Model loaded successfully');
            updateStatus('ready', 'Модель загружена');
            setModelButtons(true);
        },
        undefined,
        (error) => {
            isModelLoading = false;
            console.error('Ошибка загрузки модели:', error);
            updateStatus('error', 'Ошибка загрузки модели');
            createDebugSphere();
        }
    );
}

// ===== DEBUG SPHERE (fallback when model is missing) =====
function createDebugSphere() {
    const geom = new THREE.SphereGeometry(1, 32, 32);
    const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const sphere = new THREE.Mesh(geom, material);
    modelContainer = new THREE.Group();
    modelContainer.add(sphere);
    modelContainer.scale.setScalar(currentScale);
    modelContainer.visible = true;

    if (locar) {
        locar.add(modelContainer, TARGET_LNG, TARGET_LAT, MODEL_ALTITUDE);
        createDimensions();
        updateDimensions();
        updateDimensionText();
    }

    updateStatus('ready', 'Отладочная сфера (модель не найдена)');
}

// ===== INIT =====
export async function initAR() {
    // Timeout for initialization
    const timeoutId = setTimeout(() => {
        if (!locar) {
            updateStatus('error', 'Не удалось инициализировать AR');
        }
    }, 3000);

    await startApp();

    async function startApp() {
        const app = new App({
            cameraOptions: { hFov: 80, near: 0.001, far: 1000 }
        });

        try {
            locar = await app.start();
            clearTimeout(timeoutId);

            // Configure renderer for PBR materials
            const renderer = (locar as any).renderer as THREE.WebGLRenderer | undefined;
            if (renderer) {
                renderer.outputColorSpace = THREE.SRGBColorSpace;
                renderer.toneMapping = THREE.ACESFilmicToneMapping;
                renderer.toneMappingExposure = 1.2;
            }

            // Add lights for PBR materials
            const scene = (locar as any).scene as THREE.Scene;
            if (scene) {
                const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
                scene.add(ambientLight);

                const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
                dirLight.position.set(5, 10, 7);
                scene.add(dirLight);
            }

            updateStatus('', 'Ожидание GPS...');

            locar.on('gpserror', (error: GeolocationPositionError) => {
                const messages: Record<number, string> = {
                    1: 'Доступ к GPS запрещён',
                    2: 'Позиция не определена',
                    3: 'Таймаут GPS'
                };
                updateStatus('error', messages[error.code] || 'Ошибка GPS');
            });

            locar.on('gpsupdate', (ev: GpsReceivedEvent) => {
                userLat = ev.position.coords.latitude;
                userLng = ev.position.coords.longitude;
                userAlt = ev.position.coords.altitude;
                userAltAcc = ev.position.coords.altitudeAccuracy;

                if (!gpsReady) {
                    gpsReady = true;
                    updateStatus('ready', 'GPS готов');
                    loadModel();
                    positionModel();
                    (async () => {
                        modelAltitude = await fetchElevation(TARGET_LAT, TARGET_LNG);
                        updateCoordsInfo();
                    })();
                }

                updateCoordsInfo();
            });

            locar.startGps();

            function updateCameraPosition() {
                requestAnimationFrame(updateCameraPosition);
            }
            updateCameraPosition();

        } catch (e: any) {
            clearTimeout(timeoutId);
            updateStatus('error', e.message || 'Ошибка инициализации');
        }
    }
}

// ===== CONTROLS =====
scaleSlider.addEventListener('input', () => {
    currentScale = parseFloat(scaleSlider.value);
    updateScale();
});

btnScaleMinus.addEventListener('click', () => {
    currentScale = Math.max(0.1, Math.min(10, currentScale - 0.01));
    scaleSlider.value = currentScale.toString();
    updateScale();
});

btnScalePlus.addEventListener('click', () => {
    currentScale = Math.max(0.1, Math.min(10, currentScale + 0.01));
    scaleSlider.value = currentScale.toString();
    updateScale();
});

// ===== ROTATION CONTROLS =====
rotationSlider.addEventListener('input', () => {
    currentRotation = parseFloat(rotationSlider.value);
    updateRotation();
});

btnRotationMinus.addEventListener('click', () => {
    currentRotation = Math.max(-180, Math.min(180, currentRotation - 0.1));
    rotationSlider.value = currentRotation.toString();
    updateRotation();
});

btnRotationPlus.addEventListener('click', () => {
    currentRotation = Math.max(-180, Math.min(180, currentRotation + 0.1));
    rotationSlider.value = currentRotation.toString();
    updateRotation();
});

// ===== OFFSET LAT CONTROLS =====
offsetLatSlider.addEventListener('input', () => {
    currentOffsetLat = parseFloat(offsetLatSlider.value);
    updateOffsetLat();
});

btnOffsetLatMinus.addEventListener('click', () => {
    currentOffsetLat = Math.max(-0.001, Math.min(0.001, currentOffsetLat - 0.000001));
    offsetLatSlider.value = currentOffsetLat.toString();
    updateOffsetLat();
});

btnOffsetLatPlus.addEventListener('click', () => {
    currentOffsetLat = Math.max(-0.001, Math.min(0.001, currentOffsetLat + 0.000001));
    offsetLatSlider.value = currentOffsetLat.toString();
    updateOffsetLat();
});

// ===== OFFSET LNG CONTROLS =====
offsetLngSlider.addEventListener('input', () => {
    currentOffsetLng = parseFloat(offsetLngSlider.value);
    updateOffsetLng();
});

btnOffsetLngMinus.addEventListener('click', () => {
    currentOffsetLng = Math.max(-0.001, Math.min(0.001, currentOffsetLng - 0.000002));
    offsetLngSlider.value = currentOffsetLng.toString();
    updateOffsetLng();
});

btnOffsetLngPlus.addEventListener('click', () => {
    currentOffsetLng = Math.max(-0.001, Math.min(0.001, currentOffsetLng + 0.000002));
    offsetLngSlider.value = currentOffsetLng.toString();
    updateOffsetLng();
});

// ===== HEIGHT CONTROLS =====
heightSlider.addEventListener('input', () => {
    currentHeight = parseFloat(heightSlider.value);
    updateHeight();
});

btnHeightMinus.addEventListener('click', () => {
    currentHeight -= 0.1;
    heightSlider.value = currentHeight.toString();
    updateHeight();
});

btnHeightPlus.addEventListener('click', () => {
    currentHeight += 0.1;
    heightSlider.value = currentHeight.toString();
    updateHeight();
});

// ===== MAIN CONTROLS =====
btnReset.addEventListener('click', () => {
    resetAll();
});

btnFakeGps.addEventListener('click', toggleFakeGps);

btnToggleTools.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleToolsPanel();
});

btnCameraRotate.addEventListener('click', (e) => {
    e.stopPropagation();
    cameraRotateEnabled = !cameraRotateEnabled;
    updateCameraRotateIcon();
});

btnReplaceModel.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!userLat || !userLng || !modelContainer || !modelBasePosition) return;

    const lat = userLat + currentOffsetLat;
    const lng = userLng + currentOffsetLng;

    const metersPerDegLat = 111320;
    const metersPerDegLng = 111320 * Math.cos(toRad(lat));
    const bearingRad = THREE.MathUtils.degToRad(currentBearing);
    const dLat = (3 * Math.cos(bearingRad)) / metersPerDegLat;
    const dLng = (3 * Math.sin(bearingRad)) / metersPerDegLng;

    const newLat = lat + dLat;
    const newLng = lng + dLng;

    modelLat = newLat;
    modelLng = newLng;

    if (locar) {
        const [worldX, worldZ] = locar.lonLatToWorldCoords(newLng, newLat);
        modelContainer.position.set(worldX, currentHeight, worldZ);
        modelBasePosition = { x: modelContainer.position.x, z: modelContainer.position.z };
    }

    currentHeight = MODEL_ALTITUDE;
    heightSlider.value = currentHeight.toString();
    updateHeight();
});

// ===== TOUCH ORIENTATION =====
let isTouching = false;
let lastTouchAngle = 0;

document.addEventListener('touchstart', (e) => {
    if ((e.target as HTMLElement).closest('canvas') && e.touches.length === 1) {
        isTouching = true;
        const touch = e.touches[0];
        lastTouchAngle = Math.atan2(
            touch.clientY - window.innerHeight / 2,
            touch.clientX - window.innerWidth / 2
        );
    }
});

document.addEventListener('touchmove', (e) => {
    if ((e.target as HTMLElement).closest('canvas') && isTouching && e.touches.length === 1 && locar && gpsReady && cameraRotateEnabled) {
        const touch = e.touches[0];
        const currentAngle = Math.atan2(
            touch.clientY - window.innerHeight / 2,
            touch.clientX - window.innerWidth / 2
        );
        const angleDiff = currentAngle - lastTouchAngle;

        locar.camera.rotation.y += THREE.MathUtils.degToRad(toDeg(angleDiff));

        lastTouchAngle = currentAngle;

        if (useFakeGps) {
            updateCoordsInfo();
        }
    }
});

document.addEventListener('touchend', (e) => {
    if ((e.target as HTMLElement).closest('canvas')) {
        isTouching = false;
    }
});

// ===== MOUSE ORIENTATION (desktop) =====
let isDragging = false;
let lastMouseX = 0;

document.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement).closest('canvas')) {
        isDragging = true;
        lastMouseX = e.clientX;
    }
});

document.addEventListener('mousemove', (e) => {
    if ((e.target as HTMLElement).closest('canvas') && isDragging && locar && gpsReady && cameraRotateEnabled) {
        const deltaX = e.clientX - lastMouseX;
        locar.camera.rotation.y += THREE.MathUtils.degToRad(deltaX * 0.3);
        lastMouseX = e.clientX;

        if (useFakeGps) {
            updateCoordsInfo();
        }
    }
});

document.addEventListener('mouseup', (e) => {
    if ((e.target as HTMLElement).closest('canvas')) {
        isDragging = false;
    }
});

document.addEventListener('mouseleave', (e) => {
    if ((e.target as HTMLElement).closest('canvas')) {
        isDragging = false;
    }
});
